import React from "react";
import { useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";

import { Module, ModuleBody } from "@wfp/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSeedling } from "@fortawesome/free-solid-svg-icons";

import TableExtended from "components/TableExtended";
import MeasureUnitValue from "components/MeasureUnitValue";
import Empty from "components/Empty";
import SubTitle from "components/SubTitle";

import commoditiesDuck from "data-handler/ducks/commodities";
import deliveryIssuesDuck from "data-handler/ducks/deliveryIssues";
import { getCurrencyForCurrentSchool } from "data-handler/ducks/currency";
import { nonWfpCategory, takeHomeRationCategory } from "SCConstants";

import {
  getWaybillByCommodityAndDeliveryID,
  getPurchaseNameByCommodityPurchaseID,
} from "helpers/stock";
import { deliveryCategory, purchaseDetailCategory } from "SCConstants";
import "./_commodities-show.scss";
import { getAllStores } from "data-handler/ducks/stores";
import { getVendorCategories } from "data-handler/ducks/vendors";
import { missingCommodity } from "data-handler/ducks/reportsV3";
import { FoodSource, getFoodSources } from "data-handler/ducks/foodSources";

type CommoditySideEffect = {
  related_event_id: number;
  related_event_type?: "DEL" | "PUR";
};

type Commodity = {
  commodity: number;
  total_paid: number;
  batch_no: string;
  category: string;
  source: string;
  quantity: number;
  issues: number[];
  comments: string[];
  meta?: { batch_no?: string; comments?: string };
  side_effects?: CommoditySideEffect[];
};

type CommodityShowProps = {
  commodities: Commodity[];
  emptyTitle: React.ReactNode;
  title: React.ReactNode;
  model: string;
  type?: string;
};
export default function CommodityShow({
  commodities,
  emptyTitle,
  title,
  model,
  type,
}: CommodityShowProps) {
  const deliveryIssuesIndex = useSelector(deliveryIssuesDuck.getIndex);
  const commodityIndex = useSelector(commoditiesDuck.getIndex);
  const defaultCurrency = useSelector(getCurrencyForCurrentSchool);

  const getCommodity = (id: number) => {
    const defaultCommodity = {
      ...missingCommodity,
      name: `Unidentified(${id})`,
    };
    return commodityIndex[id] || defaultCommodity;
  };

  const allStores = useSelector(getAllStores);
  const defaultCurrencyName = defaultCurrency.name;
  const defaultCurrencyDecimals = defaultCurrency.decimals;
  const commoditiesTotalPaid = commodities?.map((i) => i.total_paid);
  const commoditiesTotalPaidSum = commoditiesTotalPaid
    ?.reduce((a: any, b: any) => parseFloat(a) + parseFloat(b), 0)
    .toFixed(defaultCurrencyDecimals);
  const InitialStockSource = type === "Carry-over" ? "Carry-over" : null;
  const vendorCategories = useSelector(getVendorCategories);

  const foodSources = useSelector(getFoodSources);

  const sourceFromId = (value: string): FoodSource | undefined => {
    if (!value) return undefined;
    const valueString = value.toString();
    const foodSource = foodSources.find((foodSource) => {
      return foodSource.id.toString() === valueString;
    });
    return foodSource;
  };

  const rows = commodities
    ? commodities.map((row, i) => {
        const commodity = getCommodity(row.commodity);

        let waybillNos: string[] = [];
        if (model === "attendance" && row.category === deliveryCategory) {
          (row.side_effects || [])
            .filter((e) => e.related_event_type === "DEL")
            .forEach((sideEffect) => {
              const waybill_no = getWaybillByCommodityAndDeliveryID(
                allStores,
                sideEffect.related_event_id
              );
              waybillNos.push(waybill_no);
            });
        }
        // Make sure there are no duplicates
        waybillNos = [...new Set(waybillNos)];

        const deliveryTarget =
          model === "incident"
            ? row.side_effects?.find((e) => e.related_event_type === "DEL")
                ?.related_event_id
            : undefined;

        const waybill_no =
          row.batch_no && !deliveryTarget && model === "incident"
            ? "Initial stock"
            : getWaybillByCommodityAndDeliveryID(
                allStores,
                parseInt((deliveryTarget || "0").toString())
              );

        let purchaseNames: string[] = [];
        if (model === "attendance" && row.category === purchaseDetailCategory) {
          (row.side_effects || [])
            .filter((e) => e.related_event_type === "PUR")
            .forEach((sideEffect) => {
              const purchaseName = getPurchaseNameByCommodityPurchaseID(
                allStores,
                sideEffect.related_event_id,
                vendorCategories,
                false
              );
              purchaseNames.push(purchaseName);
            });
        }
        // Make sure there are no duplicates
        purchaseNames = [...new Set(purchaseNames)];

        const purchaseTarget =
          model === "incident"
            ? row.side_effects?.find((e) => e.related_event_type === "PUR")
                ?.related_event_id
            : undefined;

        let purchaseName = "";
        if (
          model === "incident" &&
          row.category === purchaseDetailCategory &&
          purchaseTarget
        ) {
          purchaseName = getPurchaseNameByCommodityPurchaseID(
            allStores,
            parseInt(purchaseTarget.toString()),
            vendorCategories
          );
        }

        const commodityCategory =
          row.category === takeHomeRationCategory ? (
            <FormattedMessage
              id="source.thr"
              defaultMessage="Take-home ration"
            />
          ) : row.category === deliveryCategory ? (
            <FormattedMessage
              id="source.deliverySource"
              defaultMessage="On-site school meals (WFP)"
            />
          ) : row.category === purchaseDetailCategory ? (
            <FormattedMessage
              id="source.purchaseSource"
              defaultMessage="Store (Purchases) "
            />
          ) : row.category === nonWfpCategory ? (
            <FormattedMessage
              id="source.nonWfp"
              defaultMessage="Non-WFP food"
            />
          ) : undefined;

        return (
          <tr key={i}>
            <td className="commodities-show__commodity">
              <span className="mobile-title">
                <FormattedMessage
                  id="CommodityShow.commodityTitle"
                  defaultMessage="Commodity"
                />
              </span>
              {commodity && commodity.name}
            </td>
            {model === "delivery" && (
              <td className="commodities-show__batch">
                <span className="mobile-title">
                  <FormattedMessage
                    id="CommodityShow.batch_no"
                    defaultMessage="Batch number"
                  />
                </span>
                {row?.batch_no || row?.meta?.batch_no ? (
                  row.batch_no || row.meta?.batch_no
                ) : (
                  <FormattedMessage
                    id="DeliveriesEdit.unknownLabel"
                    defaultMessage="Unknown"
                  />
                )}
              </td>
            )}
            {(model === "attendance" || model === "delivery") && (
              <td className="commodities-show__source">
                <span className="mobile-title">
                  <FormattedMessage
                    id="CommodityShow.source"
                    defaultMessage="Source"
                  />
                </span>
                {sourceFromId(row.source)?.source ||
                  commodityCategory ||
                  InitialStockSource}
              </td>
            )}
            {model === "delivery" && type !== "Carry-over" && (
              <td>
                <span className="mobile-title">
                  <FormattedMessage
                    id="CommodityShow.quality"
                    defaultMessage="Quality"
                  />
                </span>
                {(row.issues || []).map((issue) => (
                  <p key={issue}>{deliveryIssuesIndex[issue]?.description}</p>
                ))}
              </td>
            )}
            <td className="commodities-show__quantity">
              <MeasureUnitValue measureUnit={commodity.measure_unit}>
                {row.quantity}
              </MeasureUnitValue>
            </td>
            {model === "delivery" && type !== "Carry-over" && (
              <td>
                <span className="mobile-title">
                  <FormattedMessage
                    id="DeliveriesEdit.comments"
                    defaultMessage="Comments"
                  />
                </span>
                {row.meta?.comments}
              </td>
            )}
            {model === "purchase_detail" && (
              <td>
                <span className="mobile-title">
                  <FormattedMessage
                    id="PriceShow.price"
                    defaultMessage="Amount paid"
                  />
                  {` (${defaultCurrencyName})`}
                </span>
                {Number(row.total_paid).toFixed(defaultCurrencyDecimals)}
              </td>
            )}
            {model === "incident" && row.category === deliveryCategory && (
              <td>
                <span className="mobile-title">
                  <FormattedMessage
                    id="IncidentShow.waybill"
                    defaultMessage="Waybill number"
                  />
                </span>
                {waybill_no}
              </td>
            )}
            {model === "incident" && row.category === purchaseDetailCategory && (
              <td>
                <span className="mobile-title">
                  <FormattedMessage
                    id="IncidentShow.purchase"
                    defaultMessage="Purchase"
                  />
                </span>
                {purchaseName}
              </td>
            )}
            {model === "attendance" && (
              <td className="no-shrink">
                {waybillNos.length > 0 && (
                  <span className="mobile-title">
                    <FormattedMessage
                      id="IncidentShow.waybills"
                      defaultMessage="Waybill numbers"
                    />
                  </span>
                )}
                <div className="waybills">
                  {waybillNos.map((item) => (
                    <span key={item}>{item}</span>
                  ))}
                </div>
              </td>
            )}
            {model === "attendance" && (
              <td className="no-shrink">
                {purchaseNames.length > 0 && (
                  <span className="mobile-title">
                    <FormattedMessage
                      id="IncidentShow.purchases"
                      defaultMessage="Purchases"
                    />
                  </span>
                )}
                <div className="purchases">
                  {purchaseNames.map((item) => (
                    <span key={item}>{item}</span>
                  ))}
                </div>
              </td>
            )}
          </tr>
        );
      })
    : null;

  if (!commodities || commodities.length === 0) {
    return (
      <>
        <SubTitle small> </SubTitle>
        <Module noMargin>
          <Empty
            title={emptyTitle}
            icon={<FontAwesomeIcon icon={faSeedling} size="1x" />}
          >
            <FormattedMessage
              id="CommodityShow.noCommoditiesAddedYet"
              defaultMessage="No commodities added yet"
            />
          </Empty>
        </Module>
      </>
    );
  }
  return (
    <div className="commodities-show">
      {title && <SubTitle small>{title}</SubTitle>}
      <Module noMargin>
        <ModuleBody noPadding>
          <TableExtended noBorder>
            {/* TODO: get headers from parent, don't assume `rows` shape */}
            <thead>
              <tr>
                <th>
                  <FormattedMessage
                    id="CommodityShow.commodityTitle"
                    defaultMessage="Commodity"
                  />
                </th>
                {model === "delivery" && (
                  <th>
                    <FormattedMessage
                      id="CommodityShow.batch_no"
                      defaultMessage="Batch number"
                    />
                  </th>
                )}

                {(model === "attendance" || model === "delivery") && (
                  <th>
                    <FormattedMessage
                      id="CommodityShow.source"
                      defaultMessage="Source"
                    />
                  </th>
                )}
                {model === "delivery" && type !== "Carry-over" && (
                  <th>
                    <FormattedMessage
                      id="CommodityShow.quality"
                      defaultMessage="Quality"
                    />
                  </th>
                )}
                <th>
                  <FormattedMessage
                    id="CommodityShow.quantity"
                    defaultMessage="Quantity"
                  />
                </th>
                {model === "delivery" && type !== "Carry-over" && (
                  <th>
                    <FormattedMessage
                      id="CommodityShow.comments"
                      defaultMessage="Comments"
                    />
                  </th>
                )}
                {model === "purchase_detail" && (
                  <th>
                    <FormattedMessage
                      id="PriceShow.price"
                      defaultMessage="Amount paid"
                    />
                    {` (${defaultCurrencyName})`}
                  </th>
                )}
                {model === "incident" && (
                  <th>
                    <FormattedMessage
                      id="IncidentShow.waybill"
                      defaultMessage="Waybill number"
                    />
                  </th>
                )}
                {model === "attendance" && (
                  <th className="no-shrink">
                    <FormattedMessage
                      id="IncidentShow.waybills"
                      defaultMessage="Waybill numbers"
                    />
                  </th>
                )}
                {model === "attendance" && (
                  <th className="no-shrink">
                    <FormattedMessage
                      id="IncidentShow.purchases"
                      defaultMessage="Purchases"
                    />
                  </th>
                )}
              </tr>
            </thead>
            <tbody>{rows}</tbody>
            {model === "purchase_detail" && (
              <thead>
                <th>
                  <FormattedMessage
                    id="TotalPriceShow.totalPrice"
                    defaultMessage="Total amount paid"
                  />
                  {` (${defaultCurrencyName})`}
                </th>
                <td> {} </td>
                <td> {commoditiesTotalPaidSum} </td>
              </thead>
            )}
          </TableExtended>
        </ModuleBody>
      </Module>
    </div>
  );
}
